import React, { useState, useEffect } from 'react';
import { X, Save, RotateCcw, Bot, Info } from 'lucide-react';

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  onUndo?: () => void;
  onAskAI?: (field: string) => void;
  title: string;
  children: React.ReactNode;
  showAIButton?: boolean;
  showUndoButton?: boolean;
  initialData?: any;
  fullscreen?: boolean;
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUndo,
  onAskAI,
  title,
  children,
  showAIButton = false,
  showUndoButton = false,
  initialData,
  fullscreen = false
}) => {
  const [data, setData] = useState(initialData);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const handleSave = () => {
    onSave(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative bg-card border border-border shadow-xl flex flex-col ${
        fullscreen 
          ? 'w-full h-full rounded-none' 
          : 'max-w-2xl w-full mx-4 max-h-[90vh] rounded-lg'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {children}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            {showUndoButton && onUndo && (
              <button
                onClick={onUndo}
                className="btn-secondary flex items-center gap-2"
              >
                <RotateCcw size={16} />
                Deshacer
              </button>
            )}
            {showAIButton && onAskAI && (
              <button
                onClick={() => onAskAI('field')}
                className="btn-secondary flex items-center gap-2"
              >
                <Bot size={16} />
                Preguntar IA
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="btn-primary flex items-center gap-2"
            >
              <Save size={16} />
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal; 